import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { LoaderCircle } from 'lucide-react';

import { ApiError } from '@/api/client';
import { useAdjustStock } from '@/api/hooks/useMedicine';
import type { Medicine } from '@/api/types';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/use-toast';

/**
 * Corrects the stock count directly — shrinkage, breakage, a stock-take that
 * disagrees with the system. Distinct from Restock, which adds a dated batch
 * for a real delivery; this can move the count in either direction and is
 * always logged as an Adjust movement.
 */
export function AdjustStockDialog({
  medicine,
  open,
  onOpenChange,
}: {
  medicine: Medicine | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const adjustStock = useAdjustStock();

  const [newStock, setNewStock] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && medicine) {
      setNewStock(String(medicine.stock));
      setNote('');
      setError(null);
    }
  }, [open, medicine]);

  if (!medicine) return null;

  const delta = Number(newStock) - medicine.stock;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    const value = Number(newStock);
    if (!Number.isFinite(value) || value < 0) {
      setError('Enter a valid stock count.');
      return;
    }

    if (value === medicine!.stock) {
      onOpenChange(false);
      return;
    }

    try {
      await adjustStock.mutateAsync({
        id: medicine!.id,
        request: { stock: value, type: 'Adjust', note: note.trim() || undefined },
      });
      toast({
        title: 'Stock adjusted',
        description: `${medicine!.name}: ${medicine!.stock} → ${value} ${medicine!.unit}.`,
        variant: 'success',
      });
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Please try again.');
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Adjust stock — {medicine.name}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <div className="space-y-1.5">
            <Label htmlFor="a-stock">New stock count *</Label>
            <Input
              id="a-stock"
              type="number"
              min={0}
              autoFocus
              value={newStock}
              onChange={(e) => setNewStock(e.target.value)}
              required
            />
            {Number.isFinite(delta) && delta !== 0 && (
              <p className="text-xs text-muted-foreground">
                {delta > 0 ? '+' : ''}
                {delta} {medicine.unit} from current stock
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="a-note">Reason</Label>
            <Input
              id="a-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Damaged in storage, stock-take correction"
            />
          </div>

          {error && (
            <p role="alert" className="rounded-md border border-status-expired/30 bg-status-expired/10 px-3 py-2 text-sm text-status-expired">
              {error}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={adjustStock.isPending}>
              {adjustStock.isPending && <LoaderCircle className="animate-spin" />}
              Save adjustment
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
